import { FC } from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { Button, Icon, LinkButton } from 'src/components';
import { PathBeforeNavigate } from 'src/apps/Path/PathContinueButtons';
import { PathSessionType } from 'src/apps/Path/types';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import { convertUrlToTriggerRefresh } from 'src/utils/convertUrlToTriggerRefresh';
import { getAprenderMixedSessionUrl } from 'src/utils/getAprenderMixedSessionUrl';
import theme from 'src/utils/theme';
import { EndOfSessionTrack } from '../tracking';
import * as S from './styles';

const msgs = defineMessages({
  continue: { id: 'Continue' },
  continueLearning: { id: 'path_continue_learning_cta', defaultMessage: 'Continue learning' },
  continueReviewing: { id: 'scenario_end_of_session_continue_reviewing' },
  goBackToPath: { id: 'path_go_back_cta', defaultMessage: 'Go back to my path' },
  done: { id: 'blob_eos_done', defaultMessage: 'Done' },
  doneForToday: { id: 'blob_eos_done_for_today', defaultMessage: 'Done for today' },
});

type ActionsProps = {
  languagePairId: number | null;
  pathwayNodeId: number | null;
  /** Set for learn and mixed sessions, null when there is no learning to continue */
  pathSessionType: PathSessionType | null;
  continueReviewUrl: string | null;
  hasReachedDailyGoal: boolean;
  onBeforeExit: PathBeforeNavigate;
  track: EndOfSessionTrack;
};

const ContinueLabel: FC<{ children: string }> = ({ children }) => (
  <S.ContinueLabel>
    {children}
    <Icon name="forward" />
  </S.ContinueLabel>
);

export const Actions: FC<ActionsProps> = ({
  continueReviewUrl,
  hasReachedDailyGoal,
  onBeforeExit,
  track,
  languagePairId,
  pathwayNodeId,
  pathSessionType,
}) => {
  const intl = useIntl();
  const { safePush } = useSafePush();
  const isMobile = useMediaQuery({ maxWidth: theme.sz.md });
  const canContinueLearning =
    languagePairId !== null && (pathwayNodeId !== null || pathSessionType !== null);
  const canContinue = !!continueReviewUrl || canContinueLearning;
  const exit = (trigger: 'done_button' | 'go_back_link') => {
    track('clicked_exit', { trigger });
    onBeforeExit(() => safePush(ROUTES.path), 'back');
  };

  /* Once the daily goal is reached, "Done" sits next to Continue and labels are shortened on
   * mobile so both fit on one row. */
  const isCompact = hasReachedDailyGoal && isMobile;
  const continueLabel = (fullLabel: MessageDescriptor) =>
    intl.formatMessage(isCompact ? msgs.continue : fullLabel);

  const renderPrimary = () => {
    if (continueReviewUrl) {
      return (
        <LinkButton
          buttonType="primary"
          buttonSize="large"
          wide
          href={convertUrlToTriggerRefresh(continueReviewUrl)}
          onClick={() => track('clicked_continue', { destination: 'review' })}
          data-testid="blob-eos-continue"
        >
          <ContinueLabel>{continueLabel(msgs.continueReviewing)}</ContinueLabel>
        </LinkButton>
      );
    }

    if (canContinueLearning) {
      // The blob path has no pathway nodes to advance: like its Learn button, continuing
      // starts the next mixed session
      const nextSessionUrl = convertUrlToTriggerRefresh(
        getAprenderMixedSessionUrl(languagePairId as number),
      );
      return (
        <Button
          buttonType="primary"
          buttonSize="large"
          wide
          onClick={() => {
            track('clicked_continue', { destination: 'path' });
            onBeforeExit(() => safePush(nextSessionUrl), 'continue');
          }}
          data-testid="blob-eos-continue"
        >
          <ContinueLabel>{continueLabel(msgs.continueLearning)}</ContinueLabel>
        </Button>
      );
    }

    return (
      <Button buttonType="primary" buttonSize="large" wide onClick={() => exit('go_back_link')}>
        {intl.formatMessage(msgs.goBackToPath)}
      </Button>
    );
  };

  if (hasReachedDailyGoal && canContinue) {
    return (
      <S.Row data-testid="blob-eos-action-row">
        <S.DoneButton>
          <Button
            buttonType="secondary"
            buttonSize="large"
            wide
            noWrap={!isMobile}
            onClick={() => exit('done_button')}
            data-testid="blob-eos-done"
          >
            {intl.formatMessage(isMobile ? msgs.done : msgs.doneForToday)}
          </Button>
        </S.DoneButton>
        <S.Primary>{renderPrimary()}</S.Primary>
      </S.Row>
    );
  }

  return (
    <S.Container>
      {renderPrimary()}
      {canContinue && (
        <S.TextButton type="button" onClick={() => exit('go_back_link')}>
          {intl.formatMessage(msgs.goBackToPath)}
        </S.TextButton>
      )}
    </S.Container>
  );
};
